/**
 * App-level keyboard dispatcher (spec §17). One `keydown` listener on `window`; the App hands it
 * a small context describing the shell UI it cannot see through the store (open menus, the
 * keys dialog, the search box, whether the reader is split).
 *
 * Letters match by `code` or lower-cased `key` (`keyIs`), so they work on an Arabic layout.
 * Chords (`g …`, `* …`) expire after 1200 ms.
 */
import { archivePath, draftsPath, inboxPath, sentPath, trashPath } from '$lib/mail/folders';
import { dialog, store } from '$lib/store';
import { Chord, isEditable, isHelpKey, isStarKey, keyIs, shouldIgnoreShortcut } from '$lib/util/keyboard';
import { canArchiveIn, deleteUids, markSeen, rowUids, toggleStar } from './_local';

export interface KeyContext {
  keysOpen(): boolean;
  setKeysOpen(open: boolean): void;
  folderMenuOpen(): boolean;
  closeFolderMenu(): void;
  /** Docked compose is showing (desktop, screen = compose). */
  dockOpen(): boolean;
  closeDock(): void;
  /** Reader is shown next to the list (desktop split). */
  isSplit(): boolean;
  searchEl(): HTMLInputElement | null;
}

export type KeyHandler = (ev: KeyboardEvent) => void;

/** Escape cascade (spec §17): first applicable step wins; returns whether anything happened. */
export function escapeCascade(ctx: KeyContext): boolean {
  if (ctx.folderMenuOpen()) {
    ctx.closeFolderMenu();
    return true;
  }
  if (ctx.dockOpen()) {
    ctx.closeDock();
    return true;
  }
  if (store.snoozeTarget) {
    store.snoozeTarget = null;
    return true;
  }
  if (store.moveSheet) {
    store.moveSheet = false;
    return true;
  }
  if (store.viewSheet) {
    store.viewSheet = false;
    return true;
  }
  if (store.foldersSheet) {
    store.foldersSheet = false;
    return true;
  }
  if (store.selection.size > 0) {
    store.clearSelection();
    return true;
  }
  const search = ctx.searchEl();
  if (store.searchQ) {
    store.clearSearch();
    search?.blur();
    return true;
  }
  if (search && typeof document !== 'undefined' && document.activeElement === search) {
    search.blur();
    return true;
  }
  if (store.screen.name === 'read' && !ctx.isSplit()) {
    store.backToList();
    return true;
  }
  return false;
}

export function createKeyHandler(ctx: KeyContext): KeyHandler {
  const gChord = new Chord();
  const starChord = new Chord();

  const listContext = () => {
    const sc = store.screen;
    if (sc.name === 'list') return true;
    if (sc.name === 'read' && ctx.isSplit()) return true;
    return false;
  };

  const openCursor = () => {
    const row = store.cursorRow();
    if (row) void store.openMessage(row.uid, row.folder);
  };

  return (ev: KeyboardEvent) => {
    if (shouldIgnoreShortcut(ev, dialog.isOpen)) return;

    if (ctx.keysOpen()) {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ctx.setKeysOpen(false);
      }
      return;
    }
    if (store.viewerOpen) {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        store.viewerOpen = false;
      }
      return;
    }
    if (ev.key === 'Escape') {
      if (escapeCascade(ctx)) ev.preventDefault();
      gChord.reset();
      starChord.reset();
      return;
    }

    const sc = store.screen;
    if (isEditable(ev.target, ev) || sc.name === 'compose' || sc.name === 'settings' || sc.name === 'contacts') return;

    // ---- chords
    if (gChord.active()) {
      gChord.consume();
      const folders = store.folders;
      const go = (path: string | null) => {
        if (path) void store.openFolder(path);
      };
      if (keyIs(ev, 'i')) go(inboxPath(folders));
      else if (keyIs(ev, 't')) go(sentPath(folders));
      else if (keyIs(ev, 'd')) go(draftsPath(folders));
      else if (keyIs(ev, 'k')) go(trashPath(folders));
      else if (keyIs(ev, 'a')) go(archivePath(folders));
      else if (keyIs(ev, 'c')) store.openContacts();
      else return;
      ev.preventDefault();
      return;
    }
    if (starChord.active()) {
      starChord.consume();
      if (keyIs(ev, 'a')) store.selectAllShown();
      else if (keyIs(ev, 'n')) store.clearSelection();
      else if (keyIs(ev, 'r')) store.selectWhere((f) => f.includes('\\Seen'));
      else if (keyIs(ev, 'u')) store.selectWhere((f) => !f.includes('\\Seen'));
      else if (keyIs(ev, 's')) store.selectWhere((f) => f.includes('\\Flagged'));
      else return;
      ev.preventDefault();
      return;
    }
    if (isStarKey(ev)) {
      starChord.arm();
      ev.preventDefault();
      return;
    }
    if (isHelpKey(ev)) {
      ev.preventDefault();
      ctx.setKeysOpen(!ctx.keysOpen());
      return;
    }
    if (!ev.shiftKey && keyIs(ev, 'g')) {
      gChord.arm();
      ev.preventDefault();
      return;
    }

    // ---- global single keys
    if (ev.key === '/') {
      ev.preventDefault();
      ctx.searchEl()?.focus();
      return;
    }
    if (!ev.shiftKey && keyIs(ev, 'c')) {
      ev.preventDefault();
      store.openCompose();
      return;
    }
    if (!ev.shiftKey && keyIs(ev, 'z')) {
      if (store.undoToken) {
        ev.preventDefault();
        void store.undo();
      }
      return;
    }
    if (ev.shiftKey && (keyIs(ev, 'i') || keyIs(ev, 'u'))) {
      const seen = keyIs(ev, 'i');
      if (sc.name === 'read' && !ctx.isSplit()) void markSeen([sc.uid], seen, sc.folder);
      else if (sc.name === 'read') void markSeen([sc.uid], seen, sc.folder);
      else if (sc.name === 'list') void markSeen(store.cursorUids(), seen);
      else return;
      ev.preventDefault();
      return;
    }

    // ---- reader (not split)
    if (sc.name === 'read' && !ctx.isSplit()) {
      if (keyIs(ev, 'u')) {
        ev.preventDefault();
        store.backToList();
        return;
      }
      if (keyIs(ev, 'j') && !ev.repeat) {
        ev.preventDefault();
        void store.openNeighbor(1);
        return;
      }
      if (keyIs(ev, 'k') && !ev.repeat) {
        ev.preventDefault();
        void store.openNeighbor(-1);
        return;
      }
      return;
    }

    // ---- list or split
    if (!listContext()) return;
    const split = ctx.isSplit();
    if (keyIs(ev, 'j') || ev.key === 'ArrowDown') {
      ev.preventDefault();
      store.moveCursor(1);
      if (split) store.previewCursor();
      return;
    }
    if (keyIs(ev, 'k') || ev.key === 'ArrowUp') {
      ev.preventDefault();
      store.moveCursor(-1);
      if (split) store.previewCursor();
      return;
    }
    if (ev.key === 'Home') {
      ev.preventDefault();
      store.cursorTo('start');
      if (split) store.previewCursor();
      return;
    }
    if (ev.key === 'End') {
      ev.preventDefault();
      store.cursorTo('end');
      if (split) store.previewCursor();
      return;
    }
    const row = store.cursorRow();
    if (!row) return;
    if (ev.key === 'Enter' || keyIs(ev, 'o')) {
      ev.preventDefault();
      openCursor();
      return;
    }
    if (keyIs(ev, 'x')) {
      ev.preventDefault();
      store.toggleRow(row);
      store.lastAnchorKey = row.key;
      return;
    }
    if (keyIs(ev, 's')) {
      ev.preventDefault();
      void toggleStar(rowUids(row), row.folder);
      return;
    }
    if (keyIs(ev, 'e') && !ev.repeat) {
      if (canArchiveIn(row.folder)) {
        ev.preventDefault();
        void store.archive(rowUids(row), row.folder);
      }
      return;
    }
    if (keyIs(ev, 'b') && !ev.repeat) {
      ev.preventDefault();
      store.snoozeTarget = rowUids(row);
      return;
    }
    if (keyIs(ev, 'm') && !ev.repeat) {
      ev.preventDefault();
      void store.muteThread(row.uid, row.folder);
      return;
    }
    if ((ev.key === '#' || (ev.shiftKey && ev.code === 'Digit3')) && !ev.repeat) {
      ev.preventDefault();
      void deleteUids(rowUids(row), row.folder);
    }
  };
}
